import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Star, ChevronLeft, LayoutGrid, Sparkles, Loader2, MapPin, Clock, Info, ArrowRight, User, DollarSign, MessageSquare, Home, Heart, Briefcase, Users, Skull, Plane, Award, UsersRound, Eye } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@/components/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HOUSE_ICONS = {
  1: User, 2: DollarSign, 3: MessageSquare, 4: Home, 5: Heart, 6: Briefcase,
  7: Users, 8: Skull, 9: Plane, 10: Award, 11: UsersRound, 12: Eye
};

const HOUSE_COLORS = [
  '#FF4444', '#4CAF50', '#FFEB3B', '#90CAF9', '#FF9800', '#8BC34A',
  '#E91E63', '#9C27B0', '#673AB7', '#607D8B', '#00BCD4', '#3F51B5'
];

const HouseCard = ({ houseNum, house, index }) => {
  const Icon = HOUSE_ICONS[houseNum] || Star;
  const color = HOUSE_COLORS[houseNum - 1] || '#888';
  const hasplanets = house.planets && house.planets.length > 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.05 }}
      className={`
        relative bg-white rounded-xl border overflow-hidden
        ${hasplanets ? 'border-sky-300 ring-1 ring-cyan-500/20' : 'border-sky-200'}
      `}
    >
      {/* Intensity indicator */}
      {house.intensity > 0 && (
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 text-xs font-bold bg-cyan-500/20 text-sky-600 rounded-full">
            {house.intensity} {house.intensity > 1 ? 'planètes' : 'planète'}
          </span>
        </div>
      )}
      
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20`, border: `1px solid ${color}40` }}>
            <Icon className="h-5 w-5" style={{ color }} />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">Maison {houseNum}</h4>
            <p className="text-xs text-gray-600">{house.domain}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg" style={{ color: house.sign?.color }}>{house.sign?.symbol}</span>
          <span className="text-sm text-gray-700">{house.sign?.name}</span>
          <span className="text-xs text-gray-500">{house.degree}°</span>
        </div>
        
        {house.planets && house.planets.length > 0 && (
          <div className="mt-3 pt-3 border-t border-sky-200">
            <p className="text-xs text-gray-500 mb-2">Planètes présentes :</p>
            <div className="flex flex-wrap gap-1">
              {house.planets.map((p, i) => (
                <span key={i} className="px-2 py-1 text-xs bg-cyan-500/20 text-sky-600 rounded-full">
                  {p.symbol} {p.name}
                </span>
              ))}
            </div>
          </div>
        )}
        
        <p className="text-xs text-gray-500 mt-3 italic">{house.question}</p>
      </div>
    </motion.div>
  );
};

const RadarChart = ({ houses }) => {
  // Simple radar visualization
  const maxIntensity = Math.max(...Object.values(houses).map(h => h.intensity || 0), 1);
  
  return (
    <div className="relative w-64 h-64 mx-auto">
      {/* Circle background */}
      <div className="absolute inset-0 rounded-full border border-sky-200" />
      <div className="absolute inset-8 rounded-full border border-sky-200/50" />
      <div className="absolute inset-16 rounded-full border border-sky-200/30" />
      
      {/* House indicators */}
      {Object.entries(houses).map(([num, house], i) => {
        const angle = (i * 30 - 90) * (Math.PI / 180);
        const intensity = (house.intensity || 0) / maxIntensity;
        const distance = 90 + intensity * 30;
        const x = 128 + Math.cos(angle) * distance;
        const y = 128 + Math.sin(angle) * distance;
        const color = HOUSE_COLORS[parseInt(num) - 1];
        
        return (
          <div
            key={num}
            className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all"
            style={{ left: x, top: y }}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${house.intensity > 0 ? 'ring-2 ring-cyan-400' : ''}`}
              style={{ backgroundColor: `${color}40`, color: color, border: `2px solid ${color}` }}
            >
              {num}
            </div>
          </div>
        );
      })}
      
      {/* Center label */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs text-gray-500">Domaines</span>
      </div>
    </div>
  );
};

const AstrozodiacHousesPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    birthDate: '',
    birthTime: '',
    birthCity: 'Paris',
    birthCountry: 'FR'
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [interpretation, setInterpretation] = useState(null);
  const [loadingInterpretation, setLoadingInterpretation] = useState(false);
  const [profilePreFilled, setProfilePreFilled] = useState(false);

  // Auto-fill from user profile
  useEffect(() => {
    if (isAuthenticated && user && !profilePreFilled) {
      const newFormData = { ...formData };
      let hasPreFilled = false;
      
      if (user.first_name || user.name) {
        newFormData.firstName = user.first_name || user.name;
        hasPreFilled = true;
      }
      if (user.birth_date) {
        newFormData.birthDate = user.birth_date;
        hasPreFilled = true;
      }
      if (user.birth_time) {
        newFormData.birthTime = user.birth_time;
        hasPreFilled = true;
      }
      if (user.birth_city) {
        newFormData.birthCity = user.birth_city;
        hasPreFilled = true;
      }
      
      if (hasPreFilled) {
        setFormData(newFormData);
        setProfilePreFilled(true);
        toast.success('Données pré-remplies depuis votre profil !', {
          description: 'Modifiez si nécessaire'
        });
      }
    }
  }, [isAuthenticated, user, profilePreFilled]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.birthDate || !formData.birthCity) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    setResult(null);
    setInterpretation(null);
    
    try {
      const response = await fetch(`${API_URL}/api/astrozodiac/houses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          birth_date: formData.birthDate,
          birth_time: formData.birthTime || null,
          birth_city: formData.birthCity,
          birth_country: formData.birthCountry
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setResult(data);
        toast.success('Maisons calculées !');
      } else {
        toast.error(data.detail || 'Erreur de calcul');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };
  
  const getInterpretation = async () => {
    if (!result) return;
    
    setLoadingInterpretation(true);
    
    try {
      const response = await fetch(`${API_URL}/api/astrozodiac/interpret/houses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chart_data: result,
          focus: 'houses',
          first_name: formData.firstName
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setInterpretation(data.interpretation);
        toast.success('Interprétation générée !');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'interprétation');
    } finally {
      setLoadingInterpretation(false);
    }
  };
  
  // Find most active houses
  const getActiveHouses = () => {
    if (!result?.houses) return [];
    return Object.entries(result.houses)
      .filter(([_, h]) => h.intensity > 0)
      .sort((a, b) => b[1].intensity - a[1].intensity)
      .slice(0, 3);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-cyan-50">
      {/* Starfield */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div key={i} className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.4 + 0.1, animationDelay: `${Math.random() * 3}s` }} />
        ))}
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-sky-100">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/astrozodiac" className="flex items-center gap-2 text-sky-600 hover:text-sky-500" data-testid="back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-2">
            <LayoutGrid className="h-5 w-5 text-sky-600" />
            <span className="font-bold text-gray-900">Domaines de Vie</span>
          </div>
        </div>
      </header>
      
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Form */}
        {!result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-sky-200 mb-4">
                <LayoutGrid className="h-10 w-10 text-sky-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Tes 12 Domaines de Vie</h1>
              <p className="text-gray-600">Découvre où se concentre ton énergie vitale</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5 bg-white p-6 rounded-2xl border border-sky-200">
              <div>
                <Label className="text-gray-700">Prénom *</Label>
                <Input type="text" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-firstname" />
              </div>
              
              <div>
                <Label className="text-gray-700">Date de naissance *</Label>
                <Input type="date" value={formData.birthDate} onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-birthdate" />
              </div>
              
              <div>
                <Label className="text-gray-700 flex items-center gap-2"><Clock className="h-4 w-4" /> Heure (recommandé)</Label>
                <Input type="time" value={formData.birthTime} onChange={(e) => setFormData({ ...formData, birthTime: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-birthtime" />
                <p className="text-xs text-amber-400 mt-1 flex items-center gap-1"><Info className="h-3 w-3" /> Les maisons sont très sensibles à l'heure</p>
              </div>
              
              <div>
                <Label className="text-gray-700 flex items-center gap-2"><MapPin className="h-4 w-4" /> Ville *</Label>
                <Input type="text" value={formData.birthCity} onChange={(e) => setFormData({ ...formData, birthCity: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-city" />
              </div>
              
              <Button type="submit" disabled={loading}
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-gray-900 font-semibold rounded-xl py-6"
                data-testid="calculate-btn">
                {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Calcul...</> : <><Sparkles className="mr-2 h-5 w-5" />Analyser mes domaines</>}
              </Button>
            </form>
          </motion.div>
        )}
        
        {/* Results */}
        {result && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Domaines de Vie de {formData.firstName}</h2>
              {!result.has_birth_time && (
                <p className="text-amber-400 text-sm flex items-center justify-center gap-2">
                  <Info className="h-4 w-4" /> Sans heure de naissance, les maisons sont approximatives
                </p>
              )}
            </div>
            
            {/* Radar + Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-sky-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Radar de Vie</h3>
                <RadarChart houses={result.houses} />
              </div>
              
              <div className="bg-white rounded-2xl border border-sky-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Zones Actives</h3>
                {getActiveHouses().length > 0 ? (
                  <div className="space-y-3">
                    {getActiveHouses().map(([num, house]) => {
                      const Icon = HOUSE_ICONS[parseInt(num)];
                      const color = HOUSE_COLORS[parseInt(num) - 1];
                      return (
                        <div key={num} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                          <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
                            <Icon className="h-5 w-5" style={{ color }} />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{house.domain}</p>
                            <p className="text-xs text-gray-600">{house.planets?.length} planète(s)</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-600 text-sm">Énergie répartie uniformément</p>
                )}
              </div>
            </div>
            
            {/* Houses Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {result.houses && Object.entries(result.houses).map(([num, house], index) => (
                <HouseCard key={num} houseNum={parseInt(num)} house={house} index={index} />
              ))}
            </div>
            
            {/* AI Interpretation */}
            <div className="bg-white rounded-2xl border border-sky-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-sky-600" /> Interprétation IA
              </h3>
              
              {interpretation ? (
                <div className="prose prose-sm max-w-none"><ReactMarkdown>{interpretation}</ReactMarkdown></div>
              ) : (
                <div className="text-center py-4">
                  <Button onClick={getInterpretation} disabled={loadingInterpretation}
                    className="bg-gradient-to-r from-sky-500 to-cyan-500 text-gray-900 rounded-xl" data-testid="get-interpretation-btn">
                    {loadingInterpretation ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Analyse...</> : <><Sparkles className="mr-2 h-5 w-5" />Obtenir l'interprétation IA</>}
                  </Button>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="outline" onClick={() => { setResult(null); setInterpretation(null); }}
                className="border-gray-200 text-gray-700 hover:bg-white rounded-xl" data-testid="new-calc-btn">
                Nouveau calcul
              </Button>
              <Button onClick={() => navigate('/astrozodiac/aspects')}
                className="bg-gradient-to-r from-rose-500 to-pink-500 text-gray-900 rounded-xl" data-testid="go-aspects-btn">
                Voir mes aspects <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default AstrozodiacHousesPage;
